import { ILLUMINATION_BANDS } from 'src/components/Scenes/helpers/init_device_conditions';

export interface ZoneProperty {
	title: string;
	value: string;
}

export interface LuminosityOption {
	title: string;
	value: string;
}

/**
 * Create zone property options for zone detection mode
 */
export function createZonePropertyOptions(formatFn: any): ZoneProperty[] {
	return [
		{
			title: formatFn('zone_enter', { default: 'Enter Zone' }),
			value: 'enter',
		},
		{
			title: formatFn('zone_leave', { default: 'Leave Zone' }),
			value: 'leave',
		},
		{
			title: formatFn('zone_presence', {
				default: 'Presence in Zone',
			}),
			value: 'presence',
		},
		{
			title: formatFn('zone_absence', { default: 'Absence in Zone' }),
			value: 'absence',
		},
		{
			title: formatFn('zone_object_count', {
				default: 'Object Count in Zone',
			}),
			value: 'object_count',
		},
	];
}

const BAND_I18N: Record<string, { key: string; default: string }> = {
	dark: { key: 'alarm_state_dark', default: 'Dark' },
	twilight: { key: 'alarm_state_twilight', default: 'Twilight' },
	bright: { key: 'alarm_state_bright', default: 'Bright' },
};

/**
 * Create luminosity options for luminosity mode — three discrete bands reusing
 * the same `alarm_state_*` i18n keys used by door/motion `_LUX` sensors.
 */
export function createLuminosityOptions(formatFn: any): LuminosityOption[] {
	return ILLUMINATION_BANDS.map((band) => {
		const i18n = BAND_I18N[band] ?? { key: band, default: band };
		return {
			title: formatFn(i18n.key, { default: i18n.default }),
			value: band,
		};
	});
}

/**
 * Less than / More than operators for numeric lux (`lux_i`), aligned with `_ILLUMINATION_NUMBER`.
 */
export function createIlluminanceOperatorOptions(formatFn: any): ZoneProperty[] {
	return [
		{
			title: formatFn('less_than', { default: 'Less than' }),
			value: 'lt',
		},
		{
			title: formatFn('more_than', { default: 'More than' }),
			value: 'gt',
		},
	];
}

/**
 * Handle zone selection and reset dependent properties
 */
export function handleZoneSelection(zoneValue: string, sceneData: any): void {
	// Reset condition when zone changes
	if (sceneData._device_condition) {
		sceneData._device_condition.operator = '';
		sceneData._device_condition.value = '0';
	}
}

/**
 * Handle zone property selection and trigger condition building
 */
export function handleZonePropertySelection(property: string, onConditionChange: () => void): void {
	// Trigger condition building after property selection
	onConditionChange();
}

/**
 * Get input step value based on selected mode and property
 */
export function getInputStepValue(selectedPresenceMode: string, selectedZoneProperty: string): number {
	if (selectedPresenceMode === 'zone_detection' && selectedZoneProperty === 'object_count') {
		return 1;
	}
	if (selectedPresenceMode === 'illuminance_threshold') {
		return 1;
	}
	return 1;
}

/**
 * Get default value for input based on selected mode and property
 */
export function getDefaultInputValue(
	selectedPresenceMode: string,
	selectedZoneProperty: string,
): string {
	if (selectedPresenceMode === 'zone_detection' && selectedZoneProperty === 'object_count') {
		return '1';
	}
	if (selectedPresenceMode === 'illuminance_threshold') {
		return '0';
	}
	return '0';
}

/**
 * Get minimum value for input validation
 */
export function getMinInputValue(selectedPresenceMode: string, selectedZoneProperty: string): number {
	return 0;
}
